import { Button, CardActions, CardContent, Divider } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useCreateClientField } from '../../../../sic-lib/api/hooks/useClientFields';
import { FieldValidationType } from '../../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { buildCreateClientField } from '../../../../sic-lib/types/ClientField';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import ButtonLoading from '../../../Shared/ButtonLoading';
import SelectFieldType from '../SelectFieldType/SelectFieldType';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {},
}));

interface FormState {
    fieldType: FieldValidationType;
    fieldLabel: string;
    fieldIsUnique: boolean;
    fieldIsFilterable: boolean;
    formError: IErrorFormValidation | null;
    isVerifiable: boolean;
    isVerified: boolean;
}

interface Props {
    onDoneHandler: () => void;
}

export default function ClientFieldsItemCreate({ onDoneHandler }: Props) {
    const { classes } = useStyles();
    const { mutate: createClientField, isLoading } = useCreateClientField();

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        fieldType: FieldValidationType.none,
        fieldLabel: '',
        fieldIsUnique: false,
        fieldIsFilterable: false,
        formError: null,
        isVerifiable: false,
        isVerified: false,
    });

    const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        formDispatch({ [event.target.name]: event.target.checked });
    };

    const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
        formDispatch({ [event.target.name]: event.target.value });
    };

    const handleSubmit = async () => {
        try {
            const field = buildCreateClientField({
                validationType: formState.fieldType,
                label: formState.fieldLabel,
                isUnique: formState.fieldIsUnique,
                isFilterable: formState.fieldIsFilterable,
                isVerifiable: formState.isVerifiable,
                isVerified: formState.isVerified,
            });

            createClientField(field);

            onDoneHandler();
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <>
            <CardContent>
                <Grid container direction="row" wrap="wrap" spacing={1}>
                    <Grid item xs={true}>
                        <Grid container direction="column" wrap="nowrap" spacing={1}>
                            <Grid item xs="auto">
                                <SelectFieldType
                                    value={formState.fieldType}
                                    onSelectHandler={formDispatch}
                                    fullWidth
                                    SelectProps={{
                                        MenuProps: { disablePortal: true },
                                    }}
                                />
                            </Grid>
                            <Grid item xs="auto">
                                <TextField
                                    id="text_field_label"
                                    label="Field label"
                                    name="fieldLabel"
                                    fullWidth
                                    error={Boolean(formState.formError?.field === 'label')}
                                    helperText={
                                        formState.formError?.field === 'label'
                                            ? formState.formError.message
                                            : ''
                                    }
                                    value={formState.fieldLabel}
                                    onChange={handleChangeText}
                                    required
                                />
                            </Grid>
                            {/* checkboxes */}
                            <Grid item xs="auto" container direction="row">
                                <Grid item xs="auto">
                                    <FormControlLabel
                                        label="Unique value"
                                        labelPlacement="end"
                                        control={
                                            <Checkbox
                                                color="primary"
                                                name="fieldIsUnique"
                                                checked={formState.fieldIsUnique}
                                                onChange={handleChangeCheckbox}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid item xs="auto">
                                    <FormControlLabel
                                        label="Filterable field"
                                        labelPlacement="end"
                                        control={
                                            <Checkbox
                                                color="primary"
                                                name="fieldIsFilterable"
                                                checked={formState.fieldIsFilterable}
                                                onChange={handleChangeCheckbox}
                                            />
                                        }
                                    />
                                </Grid>
                                {(formState.fieldType === FieldValidationType.usMobilePhone ||
                                    formState.fieldType === FieldValidationType.email) && (
                                    <Grid item xs="auto">
                                        <FormControlLabel
                                            label="Is verifiable field"
                                            labelPlacement="end"
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isVerifiable"
                                                    checked={formState.isVerifiable}
                                                    onChange={handleChangeCheckbox}
                                                />
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
            <Divider />
            <CardActions>
                <Button color="primary" onClick={onDoneHandler}>
                    Cancel
                </Button>
                <ButtonLoading isLoading={isLoading} color="secondary" onClick={handleSubmit}>
                    Save
                </ButtonLoading>
            </CardActions>
        </>
    );
}
